import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';
import API_ENDPOINTS from '../../api/endpoints';

// Fetch Users
export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      params.append('sortBy', 'userCode');
      
      if (filters.userName) params.append('userName', filters.userName);
      if (filters.userCode) params.append('userCode', filters.userCode);
      if (filters.userQueue) params.append('userQueue', filters.userQueue);
      if (filters.userActive !== undefined && filters.userActive !== '') {
        params.append('userActive', filters.userActive);
      }
      if (filters.userIsAdmin !== undefined && filters.userIsAdmin !== '') {
        params.append('userIsAdmin', filters.userIsAdmin);
      }
      if (filters.userRole) params.append('userRole', filters.userRole);
      if (filters.loginStatus) params.append('loginStatus', filters.loginStatus);
      const response = await axiosInstance.get(`${API_ENDPOINTS.getUsers}?${params.toString()}`);
      // Check if response[0] exists (not checking status - it may be 1 or true)
      if (response.data?.response?.[0]) {
        return {
          data: response.data.response[0].data || [],
          meta: response.data.response[0].meta || { total: 0, page: 1, limit: 10, totalPages: 0 },
        };
      }
      return { data: [], meta: { total: 0, page: 1, limit: 10, totalPages: 0 } };
    } catch (error) {
      console.error('Error fetching users:', error);
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to fetch users');
    }
  }
);

// Fetch Leads
export const fetchLeads = createAsyncThunk(
  'admin/fetchLeads',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      
      if (filters.leadID) params.append('leadID', filters.leadID);
      if (filters.leadApplicationId) params.append('leadApplicationId', filters.leadApplicationId);
      if (filters.leadName) params.append('leadName', filters.leadName);
      if (filters.leadQueue) params.append('leadQueue', filters.leadQueue);
      if (filters.allocatedUserCode) params.append('leadAllocationUserCode', filters.allocatedUserCode);
      if (filters.dispositionMain) params.append('dispositionMain', filters.dispositionMain);
      if (filters.dispositionSub) params.append('dispositionSub', filters.dispositionSub);
      if (filters.markedImportant !== undefined && filters.markedImportant !== '') params.append('markedImportant', filters.markedImportant);
      if (filters.leadIsAllocated !== undefined && filters.leadIsAllocated !== '') params.append('leadIsAllocated', filters.leadIsAllocated);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.fromDate) params.append('leadCreatedTimeFrom', filters.fromDate);
      if (filters.toDate) params.append('leadCreatedTimeTo', filters.toDate);
      if (filters.allocationFromDate) params.append('leadAllocationCreatedTimeFrom', filters.allocationFromDate);
      if (filters.allocationToDate) params.append('leadAllocationCreatedTimeTo', filters.allocationToDate);
      const response = await axiosInstance.get(`${API_ENDPOINTS.getLeadData}?${params.toString()}&includeDetails=false`);
      
      if (response.data?.response?.[0]) {
        return {
          data: response.data.response[0].data || [],
          meta: response.data.response[0].meta || { total: 0, page: 1, limit: 10, totalPages: 0 },
        };
      }
      return { data: [], meta: { total: 0, page: 1, limit: 10, totalPages: 0 } };
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to fetch leads');
    }
  }
);

// Fetch RTM Data  
export const fetchRTMData = createAsyncThunk(
  'admin/fetchRTMData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.rtmData);
      
      if (response.data?.response?.[0]) {
        const data = response.data.response[0].data || [];
        // Filter out admins (tolerate rows where the flag is absent)
        return data.filter(user => user.userIsAdmin !== true);
      }
      return [];
    } catch (error) {
      console.error('Error fetching RTM data:', error);
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to fetch RTM data');
    }
  }
);

// Fetch RTM Stats
export const fetchRTMStats = createAsyncThunk(
  'admin/fetchRTMStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.rtmStats);
      
      if (response.data?.response?.[0]) {
        return response.data.response[0].data || {};
      }
      return {};
    } catch (error) {
      console.error('Error fetching RTM stats:', error);
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to fetch RTM stats');
    }
  }
);

// Update User
export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ orgId, userCode, userData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.updateUser(orgId, userCode), userData);
      
      if (response.data?.response?.[0]?.status) {
        return { userCode, userData };
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to update user');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to update user');
    }
  }
);

// Update Lead
export const updateLead = createAsyncThunk(
  'admin/updateLead',
  async ({ leadId, leadData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.updateLead(leadId), leadData);
      
      if (response.data?.response?.[0]?.status) {
        return { leadId, leadData, message: response.data.response[0].message };
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to update lead');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to update lead');
    }
  }
);

// Create Lead
export const createLead = createAsyncThunk(
  'admin/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.createLead, leadData);
      
      if (response.data?.response?.[0]?.status) {
        return response.data.response[0].message;
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to create lead');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to create lead');
    }
  }
);

// Create User
export const createUser = createAsyncThunk(
  'admin/createUser',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.createUser, userData);
      
      if (response.data?.response?.[0]?.status) {
        return response.data.response[0].data;
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to create user');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to create user');
    }
  }
);

// Reset Password
export const resetPassword = createAsyncThunk(
  'admin/resetPassword',
  async (userCode, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.resetPassword, { userCode });
      
      if (response.data?.response?.[0]?.status) {
        return response.data.response[0].data;
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to reset password');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to reset password');
    }
  }
);

// Allocate Lead
export const allocateLead = createAsyncThunk(
  'admin/allocateLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.allocateLead, { leadId });
      
      if (response.data?.response?.[0]?.status) {
        return response.data.response[0].message;
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to allocate lead');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to allocate lead');
    }
  }
);

// Mark Lead Important
export const markLeadImportant = createAsyncThunk(
  'admin/markLeadImportant',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.markLeadImportant, { leadId });
      
      if (response.data?.response?.[0]?.status) {
        return { leadId, data: response.data.response[0].data };
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to mark lead');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to mark lead');
    }
  }
);

// Unmark Lead Important
export const unmarkLeadImportant = createAsyncThunk(
  'admin/unmarkLeadImportant',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.unmarkLeadImportant, { leadId });
      
      if (response.data?.response?.[0]?.status) {
        return { leadId, data: response.data.response[0].data };
      }
      return rejectWithValue(response.data?.response?.[0]?.message || 'Failed to unmark lead');
    } catch (error) {
      return rejectWithValue(error.response?.data?.response?.[0]?.message || 'Failed to unmark lead');
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    users: [],
    usersMeta: { total: 0, page: 1, limit: 10, totalPages: 0 },
    usersLoading: false,
    usersError: null,
    
    leads: [],
    leadsMeta: { total: 0, page: 1, limit: 10, totalPages: 0 },
    leadsLoading: false,
    leadsError: null,
    
    rtmData: [],
    rtmStats: {},
    rtmLoading: false,
    rtmError: null,
  },
  reducers: {
    clearUsersError: (state) => {
      state.usersError = null;
    },
    clearLeadsError: (state) => {
      state.leadsError = null;
    },
    clearRTMError: (state) => {
      state.rtmError = null;
    },
    updateRTMData: (state, action) => {
      const updateData = action.payload;
      if (updateData.userIsAdmin === true) return;
      
      const index = state.rtmData.findIndex(user => user.userID === updateData.userID);
      if (index !== -1) {
        state.rtmData[index] = { ...state.rtmData[index], ...updateData, lastUpdated: new Date().toISOString() };
      } else {
        state.rtmData.push(updateData);
      }
    },
    setRTMData: (state, action) => {
      state.rtmData = action.payload.filter(user => user.userIsAdmin !== true);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Users
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.users = action.payload.data;
        state.usersMeta = action.payload.meta;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      })
      // Fetch Leads
      .addCase(fetchLeads.pending, (state) => {
        state.leadsLoading = true;
        state.leadsError = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.leadsLoading = false;
        state.leads = action.payload.data;
        state.leadsMeta = action.payload.meta;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.leadsLoading = false;
        state.leadsError = action.payload;
      })
      // Fetch RTM Data
      .addCase(fetchRTMData.pending, (state) => {
        state.rtmLoading = true;
        state.rtmError = null;
      })
      .addCase(fetchRTMData.fulfilled, (state, action) => {
        state.rtmLoading = false;
        state.rtmData = action.payload;
      })
      .addCase(fetchRTMData.rejected, (state, action) => {
        state.rtmLoading = false;
        state.rtmError = action.payload;
      })
      // Fetch RTM Stats
      .addCase(fetchRTMStats.fulfilled, (state, action) => {
        state.rtmStats = action.payload;
      })
      // Mark/Unmark Lead Important
      .addCase(markLeadImportant.fulfilled, (state, action) => {
        const index = state.leads.findIndex(lead => lead.leadID === action.payload.leadId);
        if (index !== -1) {
          state.leads[index].leadImportantMarkedBy = action.payload.data?.leadImportantMarkedBy;
        }
      })
      .addCase(unmarkLeadImportant.fulfilled, (state, action) => {
        const index = state.leads.findIndex(lead => lead.leadID === action.payload.leadId);
        if (index !== -1) {
          state.leads[index].leadImportantMarkedBy = action.payload.data?.leadImportantMarkedBy;
        }
      });
  },
});

export const { 
  clearUsersError, 
  clearLeadsError, 
  clearRTMError, 
  updateRTMData, 
  setRTMData 
} = adminSlice.actions;

export default adminSlice.reducer;
